/**
 * Performance Monitoring Utilities
 * Tracks Core Web Vitals and custom performance metrics
 */

export interface PerformanceMetric {
  name: string
  value: number
  rating: 'good' | 'needs-improvement' | 'poor'
  delta?: number
}

// Core Web Vitals thresholds
const THRESHOLDS = {
  FCP: { good: 1800, poor: 3000 },
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
}

type MetricName = keyof typeof THRESHOLDS

function getRating(metric: MetricName, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[metric]
  if (value <= threshold.good) return 'good'
  if (value <= threshold.poor) return 'needs-improvement'
  return 'poor'
}

export function reportWebVitals(metric: PerformanceMetric) {
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Performance] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
    })
  }

  // Send to analytics in production
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      event_label: metric.rating,
      non_interaction: true,
    })
  }

  // GA4 keeps the explicit Core Web Vitals event stream. PostHog keeps
  // performance data through its built-in $web_vitals autocapture, so avoid
  // duplicating that volume with a custom web_vitals mirror.
}

export function measurePageLoad() {
  if (typeof window === 'undefined') return

  // Wait for page to be fully loaded, then defer measurement
  window.addEventListener('load', () => {
    setTimeout(() => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming

      if (navigation && process.env.NODE_ENV === 'development') {
        const metrics = {
          dns: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),
          tcp: Math.round(navigation.connectEnd - navigation.connectStart),
          ttfb: Math.round(navigation.responseStart - navigation.requestStart),
          download: Math.round(navigation.responseEnd - navigation.responseStart),
          domProcessing: Math.round(navigation.domComplete - navigation.domInteractive),
          total: Math.round(navigation.loadEventEnd - navigation.fetchStart),
        }

        console.log('[Performance] Page Load Metrics:', metrics)
      }
    }, 100)
  })
}

// Track long tasks that block the main thread
export function trackLongTasks() {
  if (typeof window === 'undefined') return

  // Defer initialization to avoid blocking main thread
  setTimeout(() => {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) {
            // Only log in development
            if (process.env.NODE_ENV === 'development') {
              console.warn('[Performance] Long Task detected:', {
                duration: Math.round(entry.duration),
                startTime: Math.round(entry.startTime),
              })
            }

            // Send to analytics (batched)
            if (window.gtag && entry.duration > 100) { // Only report tasks >100ms
              window.gtag('event', 'long_task', {
                event_category: 'Performance',
                value: Math.round(entry.duration),
                non_interaction: true,
              })
            }
          }
        }
      })

      observer.observe({ entryTypes: ['longtask'] })
    } catch (error) {
      // PerformanceObserver not supported
    }
  }, 100)
}

// Measure component render time
export function measureRender(componentName: string, callback: () => void) {
  const startTime = performance.now()
  callback()
  const endTime = performance.now()
  const duration = endTime - startTime

  if (duration > 16) { // Longer than one frame (60fps)
    console.warn(`[Performance] Slow render - ${componentName}: ${duration.toFixed(2)}ms`)
  }
}

// Initialize performance monitoring (non-blocking)
export function initPerformanceMonitoring() {
  if (typeof window === 'undefined') return

  // Use setTimeout to defer initialization (avoid blocking)
  setTimeout(() => {
    measurePageLoad()
    trackLongTasks()
  }, 1000)

  // Report when page is hidden (user navigates away)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && process.env.NODE_ENV === 'development') {
      // Final metrics before page unload
      const entries = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
      if (entries) {
        console.log('[Performance] Session metrics:', {
          loadTime: Math.round(entries.loadEventEnd - entries.fetchStart),
          domContentLoaded: Math.round(entries.domContentLoadedEventEnd - entries.fetchStart),
        })
      }
    }
  })
}

// Type declarations for gtag
declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params: Record<string, any>) => void
  }
}
